import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectFlow, setEditorSaved } from "../../../../redux/actions/integrationActions";
import { runJQL } from "../../../../redux/actions/jiraActions";
import CheckMarkIcon from "@atlaskit/icon/core/check-mark";
import CrossCircleIcon from "@atlaskit/icon/core/cross-circle";
import Button from "@atlaskit/button/new";
import { Label } from "@atlaskit/form";
import SectionMessage from "@atlaskit/section-message";

import Select from "@atlaskit/select";
import InputWithValidation from "../../../../common/components/InputWithValidation";
import ProjectSelectList from "./ProjectSelectList";

/**
 * REMOVED
 *   { value: "issue_property_set", label: "Issue property set" },
  { value: "issue_property_deleted", label: "Issue property deleted" },
 */
const events = [
  { value: "jira:issue_created", label: "Issue created" },
  { value: "jira:issue_updated", label: "Issue updated" },
  { value: "jira:issue_deleted", label: "Issue deleted" },
  { value: "comment_created", label: "Comment created" },
  { value: "comment_updated", label: "Comment updated" },
  { value: "comment_deleted", label: "Comment deleted" },
];

const filterSelected = (events, selectedValues) => {
  return events.filter((e) => {
    return selectedValues?.includes(e.value);
  });
};

const AtlassianWebhookWidget = () => {
  const [jqlFilter, setJqlFilter] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [projectsDisabled, setProjectsDisabled] = useState(false);

  const flow = useSelector((state) => state.integration.flow);
  const jqlSearchResult = useSelector((state) => state.jira.jqlSearchResult);
  const dispatch = useDispatch();

  const projectLetterError = "Project with capital P is not allowed in the JQL filter, use lowercase project instead.";

  useEffect(() => {}, []);

  useEffect(() => {
    if (flow.trigger) {
      const triggerConfig = flow.trigger.config || {};
      setJqlFilter(triggerConfig.jqlFilter || "");
      setSelectedEvents(filterSelected(events, triggerConfig.events));
      setSelectedProjectIds(Array.isArray(triggerConfig.projectIds) ? triggerConfig.projectIds : []);
    }
  }, [flow]);

  const validateQuery = async () => {
    dispatch(runJQL(jqlFilter));
  };

  const updateTriggerConfig = (config) => {
    const updatedFlow = {
      ...flow,
      trigger: {
        ...flow.trigger,
        config: {
          ...(flow.trigger?.config || {}),
          ...config,
        },
      },
    };
    dispatch(selectFlow(updatedFlow));
    dispatch(setEditorSaved(false));
  };

  const updateJql = (e) => {
    const value = e.target.value;
    setJqlFilter(value);
    updateTriggerConfig({ jqlFilter: value });
  };

  const onChangeEvents = (e) => {
    const values = e ? e.map((evnt) => evnt.value) : [];
    setSelectedEvents(filterSelected(events, values));
    updateTriggerConfig({ events: values });
  };

  const onChangeProjects = (projectIds) => {
    setSelectedProjectIds(projectIds);
    updateTriggerConfig({ projectIds });
  };

  return (
    <div className="ihub-edit-trigger">
      <p>
        Set up this trigger to respond when something happens to a Jira issue, such as issue creation, updates, or status transitions. This
        ensures automated responses to predefined changes within your Jira workflow.
      </p>
      <Label>Choose event type(s) to trigger on</Label>
      <Select
        inputId="events"
        className="hub-create-integration-trigger-event-type"
        classNamePrefix="react-select"
        options={events}
        value={selectedEvents}
        placeholder="Choose event"
        isMulti={true}
        onChange={onChangeEvents}
      />
      <Label>Choose project(s)</Label>
      <ProjectSelectList
        inputId="jira-trigger-projects"
        selectedProjectIds={selectedProjectIds}
        onSelectProjects={onChangeProjects}
        isMulti={true}
        loadIssueTypes={false}
        placeholder="Select Jira Projects"
        isDisabled={projectsDisabled}
      />

      {projectsDisabled && (
        <SectionMessage title="" testId="section-message" appearance="warning" isDismissible>
          Update the app version to enable project selection. Update must be done before 30/5.
        </SectionMessage>
      )}
      <Label>JQL filter: (JQL-filter is required)</Label>
      <InputWithValidation
        fieldTitle={""}
        fieldName={"jqlFilter"}
        onChange={updateJql}
        inputValue={jqlFilter ? jqlFilter : ""}
        maxChar={1000}
        type="textfield"
        stringValidation={[
          {
            type: "not contain",
            word: "Project=",
            message: projectLetterError,
          },
          {
            type: "not contain",
            word: "Project =",
            message: projectLetterError,
          },
        ]}
      />

      <Button appearance="link" onClick={validateQuery} isDisabled={jqlFilter === ""}>
        Validate JQL
      </Button>
      {jqlSearchResult.isValid === true && (
        <>
          <CheckMarkIcon label="" color="#00875a" />
          {jqlSearchResult.total}
        </>
      )}
      {jqlSearchResult.isValid === false && jqlSearchResult.errors && (
        <>
          <CrossCircleIcon label="" color="#de350b" className="ihub-validate-jql" /> Invalid JQL:{" "}
          {jqlSearchResult.errors ? jqlSearchResult.errors[0] : "Invalid JQL"}
        </>
      )}
      <br></br>
      <br></br>
      <h5>JQL Clause Support</h5>
      <p>
        Only a subset of JQL clauses is supported due to limitations in Jira Webhook API. Use Conditions on each action to further filter
        out when to trigger an action
      </p>

      <h5>Supported Clauses (Left-Hand Side Values):</h5>
      <ul>
        <li>issueKey</li>
        <li>project</li>
        <li>issuetype</li>
        <li>status</li>
        <li>assignee</li>
        <li>reporter</li>
        <li>issue.property (any indexed issue property can be queried)</li>
        <li>cf[id] (for custom fields - currently only the ID of the Epic custom field is supported here)</li>
      </ul>

      <h5>Supported Operators:</h5>
      <p>=, !=, IN, and NOT IN.</p>
    </div>
  );
};
export default AtlassianWebhookWidget;
